import Adapter from './adapter'

// 将取消能力附加到 Promise，并在链式调用中继承
function attachCancelable(basePromise, cancel, adapter) {
  const setCancelable = (p) => {
    try {
      Object.defineProperty(p, 'cancel', { value: cancel, configurable: true, enumerable: false });
      Object.defineProperty(p, 'adapter', { get: () => adapter, configurable: true, enumerable: false });
    } catch {}

    const nativeThen = p.then.bind(p);
    const nativeCatch = p.catch.bind(p);
    const nativeFinally = p.finally ? p.finally.bind(p) : undefined;

    p.then = (...args) => {
      const next = nativeThen(...args);
      setCancelable(next);
      return next;
    }

    p.catch = (...args) => {
      const next = nativeCatch(...args);
      setCancelable(next);
      return next;
    }

    if (nativeFinally) {
      p.finally = (...args) => {
        const next = nativeFinally(...args);
        setCancelable(next);
        return next;
      }
    }

    return p;
  }

  return setCancelable(basePromise);
}

function getEndpoint(config, defaultConfig) {
  if (!config && !defaultConfig) {
    throw new Error('No config provided');
  }

  if (!config?.endpointConfig && !defaultConfig?.endpointConfig) {
    throw new Error('No endpointConfig provided');
  }

  let endpointConfig = config?.endpointConfig;
  let defaultEndpointConfig = defaultConfig?.endpointConfig;
  if (typeof endpointConfig === 'string') return endpointConfig;
  if (typeof defaultEndpointConfig === 'string') return defaultEndpointConfig;

  if (endpointConfig && endpointConfig.endpoint) {
    return endpointConfig.endpoint;
  }

  if (defaultEndpointConfig && defaultEndpointConfig.endpoint) {
    return defaultEndpointConfig.endpoint;
  }

  endpointConfig = endpointConfig || {};
  defaultEndpointConfig = defaultEndpointConfig || {};

  const isv = endpointConfig.isv || defaultEndpointConfig.isv || 'kd';
  const app = endpointConfig.app || defaultEndpointConfig.app;
  const source = endpointConfig.source || defaultEndpointConfig.source;
  const version = endpointConfig.version || defaultEndpointConfig.version || 'v1';
  const sourceId = endpointConfig.sourceId || defaultEndpointConfig.sourceId;
  const subSource = endpointConfig.subSource || defaultEndpointConfig.subSource;
  const action = endpointConfig.action || defaultEndpointConfig.action;

  if (!app) {
    throw new Error('Invalid config app provided');
  }

  if (!source) {
    throw new Error('Invalid config source provided');
  }

  let result = `${location.pathname}kwc/${version}/${isv}/${app}/${source}`
  if (sourceId) {
    result += `/${sourceId}`

    if (subSource) {
      result += `/${subSource}`
    }

    if (action) {
      result += `/${action}`
    }
  }

  if (!result.startsWith('/')) {
    result = `/${result}`
  }

  return result
}

function createPromiseMethod(defaultMethod = 'GET', defaultConfig = {}) {
  return function promiseCall(config = {}) {
    const fullConfig = {
      endpoint: getEndpoint(config, defaultConfig),
      method: defaultMethod,
      ...defaultConfig,
      ...config
    }
    delete fullConfig.endpointConfig;

    if (config && typeof config === 'object' && !config.endpoint) {
      fullConfig.params = config.params;
    }

    let adapter = null;
    let isSettled = false;
    let rejectRef = null;

    const promise = new Promise((resolve, reject) => {
      rejectRef = reject;
      adapter = new Adapter((result) => {
        if (isSettled) return;
        isSettled = true;
        try { adapter && adapter.disconnect(); } catch {}

        if (result.error) {
          reject(result.error);
        } else {
          resolve(result);
        }
      });

      adapter.connect();
      adapter.update(fullConfig);
    });

    const cancel = () => {
      if (!adapter) return { aborted: false, removed: false };
      let aborted = false;
      try {
        if (adapter.abortController) {
          adapter.abortController.abort();
          aborted = true;
        }
      } catch {}
      if (!isSettled && rejectRef) {
        isSettled = true;
        rejectRef({ message: 'Request canceled', status: 'CANCELED' });
      }
      try { adapter.disconnect(); } catch {}
      return { aborted, removed: false };
    }

    return attachCancelable(promise, cancel, adapter);
  }
}

export const doGet = createPromiseMethod('GET')
export const doPost = createPromiseMethod('POST')
export const doDelete = createPromiseMethod('DELETE')
export const doPut = createPromiseMethod('PUT')
export const doPatch = createPromiseMethod('PATCH')